// 语言初始化模块 - 使用 cacheManager 优化缓存管理
import Util from 'utils';
import cacheManager from 'utils/cache/cacheManager';

// 语言包加载状态与进行中的请求统一挂在 window 上，避免重复请求
if (!window.languagePackageState) {
  window.languagePackageState = {};
}

if (!window.languagePackagePromiseMap) {
  window.languagePackagePromiseMap = {};
}

const codeMap = {
  'zh-CN': 'cn',
  zh: 'cn',
  'ja-JP': 'ja',
  'ko-KR': 'ko',
  'en-US': 'en',
  'en-GB': 'en',
  'en-AU': 'en',
  'ms-MY': 'ms',
  'id-ID': 'id',
  'th-TH': 'th',
  'zh-TW': 'zh-tw',
  'ru-RU': 'ru',
  'km-KH': 'km',
  'my-MM': 'my',
  'vi-VN': 'vi',
  'lo-LA': 'lo',
  'de-DE': 'de' // 新增德语配置
};

const INTERNATIONAL_HOSTS = [
  'cxwygj.com',
  'h5.cxwygj.com',
  'intl.yofogo.com',
  'foreign.yofogo.com',
  'intl.fanxinggj.com',
  'foreign.fanxinggj.com',
  'foreignh5.uat3.yofoys.cn'
];

// 国家/地区到默认语言的映射，优先解决国际域名扫码进入时的首屏语言
const countryLanguageMap = {
  JP: 'ja',
  KR: 'ko',
  HK: 'zh-tw',
  MO: 'zh-tw',
  TW: 'zh-tw',
  US: 'en',
  CA: 'en',
  GB: 'en',
  AU: 'en',
  SG: 'en',
  MY: 'ms',
  ID: 'id',
  TH: 'th',
  VN: 'vi',
  DE: 'de',
  RU: 'ru',
  KH: 'km',
  MM: 'my',
  LA: 'lo'
};

// 域名到默认语言的映射，后续如有独立国家域名可继续补充
const hostLanguageMap = {
  'jp.yofogo.com': 'ja',
  'jp.fanxinggj.com': 'ja',
  'japan.yofogo.com': 'ja',
  'japan.fanxinggj.com': 'ja'
};
// 避免循环依赖，直接定义 Language_MAP
export const Language_MAP = window.Language_MAP || {};

// 通用方法 校验语言是否存在于枚举表中，不存在则返回cn
export const languageValid = (val) => {
  if (codeMap[val]) return val;
  if (Language_MAP[val]) return val;
  return 'cn';
};

// 从设备中获取语言选项
export const getLanguage = () => {
  let val = navigator.language;
  return languageValid(val);
};

// 统一生成语言 JSON 地址，和 index.html 中的预加载路径保持一致
export const getLanguageJsonUrl = (lng) => {
  if (!lng || lng === 'cn') return '';
  const lngType = lng.toUpperCase();
  return `${window.envConfig.HOST_JSON}/translate/language/${lngType}/translate.json`;
};

// 将语言包合并进缓存，供页面运行时直接按中文 key 命中
export const mergeLanguagePackageToCache = (lng, packageJson = {}) => {
  if (!lng || lng === 'cn' || !packageJson || typeof packageJson !== 'object') {
    return {};
  }

  const cacheKey = `translation_${lng}`;
  const translationJson = cacheManager.get(cacheKey) || {};
  const mergedTranslations = { ...translationJson, ...packageJson };

  cacheManager.set(cacheKey, mergedTranslations);
  window.translate = mergedTranslations;
  window.languagePackageState[lng] = true;

  if (cacheManager.memory) {
    cacheManager.memory.languageLoaded = true;
  }

  return mergedTranslations;
};

// 通过 XHR 请求语言 JSON，兼容当前项目老环境
const requestLanguageJson = (lng) => {
  return new Promise((resolve, reject) => {
    const url = getLanguageJsonUrl(lng);
    if (!url) {
      resolve({});
      return;
    }

    const request = new XMLHttpRequest();
    request.open('GET', url, true);
    request.responseType = 'text';
    request.onreadystatechange = function () {
      if (request.readyState !== 4) return;

      if (request.status >= 200 && request.status < 300) {
        try {
          const responseText = request.responseText || request.response || '{}';
          resolve(JSON.parse(responseText));
        } catch (error) {
          reject(error);
        }
      } else {
        reject(new Error(`语言 JSON 请求失败: ${request.status}`));
      }
    };
    request.onerror = function (error) {
      reject(error || new Error('语言 JSON 请求异常'));
    };
    request.send();
  });
};

// 语言包加载入口：优先用首屏预加载结果，再按需请求 JSON，避免直接走翻译接口
export const loadLanguagePackage = (lng, options = {}) => {
  const { force = false } = options;

  if (!lng || lng === 'cn') {
    return Promise.resolve({});
  }

  if (!force && window.languagePackageState[lng]) {
    return Promise.resolve(cacheManager.get(`translation_${lng}`) || {});
  }

  // 首屏已在 index.html 中预加载时，直接合并到缓存即可
  if (
    !force &&
    window.currentLng === lng &&
    window.translate &&
    typeof window.translate === 'object'
  ) {
    return Promise.resolve(mergeLanguagePackageToCache(lng, window.translate));
  }

  if (!force && window.languagePackagePromiseMap[lng]) {
    return window.languagePackagePromiseMap[lng];
  }

  const loadPromise = requestLanguageJson(lng)
    .then((packageJson) => mergeLanguagePackageToCache(lng, packageJson))
    .catch((error) => {
      console.error(`语言包加载失败(${lng}):`, error);
      return cacheManager.get(`translation_${lng}`) || {};
    })
    .finally(() => {
      window.languagePackagePromiseMap[lng] = null;
    });

  window.languagePackagePromiseMap[lng] = loadPromise;
  return loadPromise;
};

const getDefaultInternationalLanguage = () => {
  if (typeof window.resolveDefaultLngByContext === 'function') {
    return window.resolveDefaultLngByContext();
  }

  const urlCountry = (Util.getQueryString('country') || '').toUpperCase();
  const storageCountry = (localStorage.getItem('country') || '').toUpperCase();
  const host = window.location.host;
  const isInternationalized =
    INTERNATIONAL_HOSTS.includes(host) || window.location.hash.indexOf('isInter') !== -1;

  const hostLng = hostLanguageMap[host] || '';
  const countryLng = countryLanguageMap[urlCountry] || countryLanguageMap[storageCountry] || '';

  if (isInternationalized) {
    return hostLng || countryLng || getLanguage();
  }

  return countryLng || getLanguage();
};

/**
 * 语言初始化函数
 * 负责设置当前语言和同步缓存数据
 * 使用 cacheManager 替代直接的 localStorage 操作，提供更好的缓存管理
 */
export const initLanguage = () => {
  // 1. 初始化当前语言
  const initCurrentLng = () => {
    let lng = 'cn'; // 默认中文

    // 从URL参数获取语言设置（优先级最高）
    const urlLng = Util.getQueryString('lng');
    if (urlLng && Language_MAP[urlLng]) {
      lng = urlLng;
      localStorage.setItem('lng', lng); // 语言设置本身仍使用localStorage存储
    } else {
      const storageLng = localStorage.getItem('lng');
      const defaultIntlLng = getDefaultInternationalLanguage();

      // 用户主动切换过非中文时，优先尊重用户选择
      if (storageLng && Language_MAP[storageLng] && storageLng !== 'cn') {
        lng = storageLng;
      } else if (defaultIntlLng && Language_MAP[defaultIntlLng]) {
        // 国际站点首进场景：按域名/国家/浏览器语言给出默认语言
        lng = defaultIntlLng;
        localStorage.setItem('lng', lng);
      } else if (storageLng && Language_MAP[storageLng]) {
        // 保留原有中文兜底逻辑
        lng = storageLng;
      } else {
        // 使用浏览器语言设置
        lng = getLanguage();
      }
    }

    // 设置全局语言变量
    window.currentLng = lng;
    return lng;
  };

  const currentLng = initCurrentLng();

  // 2. 同步缓存数据
  const syncCache = () => {
    try {
      if (currentLng == 'cn') {
        console.log('中文不需要同步缓存');
        return;
      }

      // 检查window.translate是否存在并同步
      if (window.translate && typeof window.translate === 'object') {
        mergeLanguagePackageToCache(currentLng, window.translate);
      }
    } catch (error) {
      console.error('缓存同步失败:', error);
    }
  };

  // 执行缓存同步
  syncCache();

  return currentLng;
};

/**
 * 获取翻译文本
 * @param {string} text - 要翻译的文本
 * @param {boolean} save - 是否保存翻译结果
 * @returns {Promise<string>} - 翻译后的文本
 */
export const getTranslation = async (text) => {
  try {
    // 如果是中文，直接返回原文
    const currentLng = window.currentLng || 'cn';
    if (currentLng === 'cn') {
      return text;
    }

    // 构建带语言后缀的缓存键
    const cacheKey = `translation_${currentLng}`;

    // 从cacheManager获取缓存的翻译数据
    const translationJson = cacheManager.get(cacheKey) || {};

    // 检查是否已有缓存的翻译结果
    if (translationJson[text]) {
      return translationJson[text];
    }

    // TODO: 如果没有缓存的翻译结果，可以添加API调用逻辑
    // 这里保留原有的翻译API调用逻辑

    return text;
  } catch (error) {
    console.error('获取翻译失败:', error);
    return text;
  }
};

/**
 * 保存翻译结果到缓存
 * @param {string} text - 原始文本
 * @param {string} translatedText - 翻译后的文本
 */
export const saveTranslation = (text, translatedText) => {
  try {
    const currentLng = window.currentLng || 'cn';
    if (currentLng === 'cn') return;

    const cacheKey = `translation_${currentLng}`;
    const translationJson = cacheManager.get(cacheKey) || {};

    // 更新翻译数据
    translationJson[text] = translatedText;

    // 保存到缓存
    cacheManager.set(cacheKey, translationJson);
  } catch (error) {
    console.error('保存翻译结果失败:', error);
  }
};



// WEBPACK FOOTER //
// ./src/config/language/initLanguage.js